import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Uses Shopify's color-filtered response. A size-only update with the same
   * media leaves the current slideshow, zoom dialog and focus in place.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const data = event.detail?.data;
    const source = data?.html;

    if (!source) return;
    const productId = this.dataset.armmyProductId;
    const incomingProductId = String(data.newProduct?.id ?? data.productId ?? productId ?? '');
    // Variant events can bubble through sections containing several products.
    if (productId && incomingProductId !== productId && !data.newProduct) return;

    const candidates = [...source.querySelectorAll('media-gallery')];
    const galleryId = this.dataset.armmyGalleryId;
    const newMediaGallery = candidates.find((candidate) => {
      if (galleryId && candidate.dataset.armmyGalleryId !== galleryId) return false;
      return !incomingProductId || candidate.dataset.armmyProductId === incomingProductId;
    });

    if (!newMediaGallery) return;

    const sameProduct = Boolean(productId && productId === newMediaGallery.dataset.armmyProductId);
    const sameColor = sameProduct && this.dataset.armmyColor === newMediaGallery.dataset.armmyColor;
    const currentIds = this.#mediaIds(this);
    const nextIds = this.#mediaIds(newMediaGallery);
    if (sameColor && this.dataset.armmyMediaIds !== undefined &&
        newMediaGallery.dataset.armmyMediaIds !== undefined &&
        currentIds.length === nextIds.length &&
        [...currentIds].sort().join(',') === [...nextIds].sort().join(',')) return;

    const currentSlide = this.slideshow?.slides?.[this.slideshow.current];
    const currentId = currentSlide?.dataset.armmyMediaId || currentSlide?.getAttribute('slide-id');
    const index = sameColor && currentId ? nextIds.indexOf(String(currentId)) : -1;
    // The native slideshow reads initial-slide during connectedCallback. Set it
    // before insertion, so restoration does not race its scroll initialization.
    newMediaGallery.querySelector('slideshow-component')?.setAttribute('initial-slide', String(Math.max(0, index)));

    // An unchanged gallery keeps an open zoom intact. A replacement must close
    // the old native dialog so its top-layer/scroll-lock state is cleaned up.
    const zoom = this.refs.zoomDialogComponent;
    if (zoom?.refs?.dialog?.open && typeof zoom.closeDialog === 'function') zoom.closeDialog();
    this.replaceWith(newMediaGallery);
  };

  #mediaIds(gallery) {
    return (gallery.dataset.armmyMediaIds || '').split(',').map((id) => id.trim()).filter(Boolean);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
